import { useState, useEffect } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';

export function SessionTimer({ startedAt, compact = false }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt) return;
    const start = new Date(startedAt).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  if (!startedAt || elapsed <= 0) return null;

  const hours = Math.floor(elapsed / 3600);
  const mins = Math.floor((elapsed % 3600) / 60);
  const secs = elapsed % 60;
  const pad = (n) => String(n).padStart(2, '0');
  const timeStr = hours > 0 ? `${hours}:${pad(mins)}:${pad(secs)}` : `${mins}:${pad(secs)}`;

  // Thresholds: 45min warning, 60min urgent, 120min must-end
  const isWarning = elapsed >= 2700 && elapsed < 3600;
  const isUrgent = elapsed >= 3600 && elapsed < 7200;
  const isMustEnd = elapsed >= 7200;

  let bgColor = 'bg-slate-100 border-slate-200';
  let textColor = 'text-slate-600';
  let iconColor = 'text-slate-400';
  let label = 'Session Time';
  let pulse = '';

  if (isWarning) {
    bgColor = 'bg-amber-50 border-amber-300';
    textColor = 'text-amber-700';
    iconColor = 'text-amber-500';
    label = 'Wrapping Up Soon';
  } else if (isUrgent) {
    bgColor = 'bg-red-50 border-red-300';
    textColor = 'text-red-700';
    iconColor = 'text-red-500';
    label = 'Session Running Long';
  } else if (isMustEnd) {
    bgColor = 'bg-red-100 border-red-400';
    textColor = 'text-red-800';
    iconColor = 'text-red-600';
    label = 'End Session Now';
    pulse = 'animate-pulse';
  }

  if (compact) {
    return (
      <div
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border ${bgColor} ${pulse} transition-colors`}
        data-testid="session-timer-compact"
      >
        {isMustEnd ? (
          <AlertTriangle className={`w-3.5 h-3.5 ${iconColor} shrink-0`} />
        ) : (
          <Clock className={`w-3.5 h-3.5 ${iconColor} shrink-0`} />
        )}
        <p className={`text-sm font-extrabold tabular-nums ${textColor}`} style={{ fontFamily: 'Nunito' }}>
          {timeStr}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 rounded-2xl border ${bgColor} ${pulse} transition-colors`}
      data-testid="session-timer"
    >
      {isMustEnd ? (
        <AlertTriangle className={`w-5 h-5 ${iconColor} shrink-0`} />
      ) : (
        <Clock className={`w-5 h-5 ${iconColor} shrink-0`} />
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-[10px] font-bold uppercase tracking-wider ${textColor} opacity-70`}>{label}</p>
        <p className={`text-lg font-extrabold tabular-nums ${textColor}`} style={{ fontFamily: 'Nunito' }}>
          {timeStr}
        </p>
      </div>
      {(isUrgent || isMustEnd) && (
        <p className={`text-[10px] font-semibold ${textColor} max-w-[120px] leading-tight`}>
          {isMustEnd ? 'Facility records need same-day completion' : 'Try to wrap up soon'}
        </p>
      )}
    </div>
  );
}
